/**
 * Audit Log export dialog (storyboard Flow J Frame 4).
 *
 * A controlled Radix Dialog (mirrors :func:`map/dynamic-track-modal`):
 * pick CSV / JSONL and whether to include the payload column, then
 * download the *current filter* via the same-origin export GET. The GET
 * streams a ``Content-Disposition: attachment`` file (the session cookie
 * rides on the same-origin navigation; GET ⇒ no CSRF) and emits an
 * ``audit.export.created`` audit event server-side — so the export is
 * itself recorded, exactly as Flow J Frame 4 describes.
 *
 * Cross-references:
 *   - Flow J Frame 4 (export modal + "export is itself audited").
 *   - REQ-UI-022 (Audit Log surface); REQ-UI-035 (same-origin only).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import {
  type AuditEventsFilter,
  type AuditExportFormat,
  buildAuditExportUrl,
} from "@/audit/use-audit-events";

export interface AuditExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current Audit Log filter — the export covers exactly this. */
  filter: AuditEventsFilter;
  /** Result count for the current filter (shown as the export scope). */
  total: number;
}

/**
 * Trigger a same-origin file download for ``url``.
 *
 * Uses a transient anchor click rather than :func:`apiFetch` (which
 * parses JSON) — the browser handles the ``Content-Disposition``
 * attachment without navigating away. Module-private; exercised via the
 * modal's Download button in tests.
 */
function downloadFile(url: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  // An empty download attribute lets the server's Content-Disposition
  // filename win while still hinting "download, don't navigate".
  anchor.download = "";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function AuditExportModal({
  open,
  onOpenChange,
  filter,
  total,
}: AuditExportModalProps): React.ReactElement {
  const [format, setFormat] = useState<AuditExportFormat>("csv");
  const [includePayload, setIncludePayload] = useState(false);

  const handleDownload = (): void => {
    downloadFile(buildAuditExportUrl(filter, { format, includePayload }));
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-bg-overlay p-5 shadow-xl"
          data-testid="audit-export-modal"
        >
          <Dialog.Title className="text-body font-medium text-text-primary">
            Export audit log
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-body-sm text-text-secondary">
            Exports the current filter ({total} {total === 1 ? "record" : "records"}). Generating an
            export is itself recorded in the audit log (audit.export.created).
          </Dialog.Description>

          <fieldset className="mt-4 border-0 p-0">
            <legend className="text-meta uppercase tracking-wide text-text-secondary">
              Format
            </legend>
            <div className="mt-2 flex gap-4">
              {(["csv", "jsonl"] as const).map((value) => (
                <label key={value} className="flex items-center gap-2 text-body text-text-primary">
                  <input
                    type="radio"
                    name="audit-export-format"
                    value={value}
                    checked={format === value}
                    onChange={() => setFormat(value)}
                    data-testid={`audit-export-format-${value}`}
                  />
                  {value.toUpperCase()}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="mt-4 flex items-center gap-2 text-body text-text-primary">
            <input
              type="checkbox"
              checked={includePayload}
              onChange={(event) => setIncludePayload(event.target.checked)}
              data-testid="audit-export-include-payload"
            />
            Include payload column
          </label>

          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-border px-3 py-1.5 text-body-sm text-text-secondary hover:bg-bg-elevated"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleDownload}
              data-testid="audit-export-download"
              className="rounded bg-brand px-3 py-1.5 text-body-sm text-bg-base hover:bg-brand-dim"
            >
              Download
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
